import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ContentHead, Loading } from '../../lib/components';
import { ContentBox } from '../../lib/components/compound/pages/contentBox';
import { Button } from '../../lib/components/atomic/button';
import { cn, downloadBlob } from '../../lib/util';
import { useStore } from '../../hooks/useStore';
import {
  invalidateWallet,
  useCardOnboarding,
  useDownloadWalletCsv,
  useUpcomingPayments,
  useWallet,
  useWalletTransactions,
  walletErrorKind,
  WalletCsvScope
} from '../../queries/pxtraCard';
import {
  berlinYear,
  csvFilename,
  currentMonth,
  formatCents,
  formatDate,
  loadWalletPeriod,
  periodForToday,
  saveWalletPeriod,
  TransactionsCard,
  UpcomingPaymentsTable,
  upcomingSummary,
  useToday,
  WalletAlert,
  WalletKpiTile,
  WalletLoadError,
  WalletPeriod,
  WalletPeriodControl,
  walletPeriodStorageKey,
  WalletStaleNotice
} from '../../lib/components/compound/pxtraCardWallet';
import { CardOnboardingNotice } from '../../lib/components/compound/pxtraCardOnboarding';
import { ClockIcon, CoinGrowthOutlineIcon, TimeCoinsIcon, WalletIcon } from '../../lib/icons';

/**
 * The period is remembered per manager and company. The user id arrives
 * asynchronously, so the stored period is re-read whenever the key changes.
 */
const useStoredWalletPeriod = (userId: string | undefined, companyId: string, today: Date) => {
  const storageKey = walletPeriodStorageKey(userId ?? 'anonymous', companyId);
  const [state, setState] = useState(() => ({
    key: storageKey,
    period: loadWalletPeriod(storageKey, today)
  }));

  if (state.key !== storageKey) {
    setState({ key: storageKey, period: loadWalletPeriod(storageKey, today) });
  }

  const setPeriod = (period: WalletPeriod) => {
    setState({ key: storageKey, period });
    if (userId && companyId) saveWalletPeriod(storageKey, period);
  };

  return [periodForToday(state.period, today), setPeriod] as const;
};

const WalletOverview = observer(({ companyId, today }: { companyId: string; today: Date }) => {
  const { t } = useTranslation();
  const { authenticationStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const [period, setPeriod] = useStoredWalletPeriod(
    authenticationStore.currentUserId?.id,
    companyId,
    today
  );

  const walletQuery = useWallet(companyId);
  const upcomingQuery = useUpcomingPayments(companyId, period, today);
  const { data: wallet } = walletQuery;
  const { data: upcoming, isPlaceholderData: isPeriodLoading } = upcomingQuery;
  const year = berlinYear(today);
  const month = currentMonth(today);
  const transactionsQuery = useWalletTransactions(companyId, year);
  const { data: transactions } = transactionsQuery;
  const previousQuery = useWalletTransactions(
    companyId,
    year - 1,
    !!transactions?.has_previous_year
  );
  const { data: previous } = previousQuery;
  const { mutateAsync: download } = useDownloadWalletCsv(companyId);

  const onDownload = async (scope: WalletCsvScope) => {
    try {
      downloadBlob(await download(scope), csvFilename(scope));
    } catch {
      enqueueSnackbar(t('pxtraCard:csvFailed'), { variant: 'error' });
    }
  };

  const periodControl = <WalletPeriodControl period={period} today={today} onChange={setPeriod} />;

  const queryClient = useQueryClient();
  const walletFailed = walletQuery.isError && !wallet;
  const upcomingFailed = upcomingQuery.isError && !upcoming;
  const accountMissing = walletFailed && walletErrorKind(walletQuery.error) === 'account_missing';
  const retryAll = () => invalidateWallet(queryClient, companyId);

  const shortfallSubtext = () => {
    if (!upcoming || upcoming.shortfall_cents === 0) return t('pxtraCard:noShortfall');
    if (upcoming.shortfall_current_month_cents > 0 && upcoming.deadline) {
      return t('pxtraCard:shortfallOfWhichUntil', {
        amount: formatCents(upcoming.shortfall_current_month_cents),
        date: formatDate(upcoming.deadline)
      });
    }
    return upcoming.deadline
      ? t('pxtraCard:shortfallUntil', { date: formatDate(upcoming.deadline) })
      : '';
  };

  if (accountMissing) return <WalletLoadError error={walletQuery.error} onRetry={retryAll} />;

  return (
    <>
      {periodControl}

      <div
        className={cn(
          'mt-4 flex min-w-0 flex-col gap-8 transition-opacity',
          isPeriodLoading && 'opacity-60'
        )}
        aria-busy={isPeriodLoading}
      >
        {walletFailed && <WalletLoadError error={walletQuery.error} onRetry={retryAll} />}
        {upcomingFailed && (
          <WalletLoadError
            error={upcomingQuery.error}
            titleKey="pxtraCard:upcomingLoadFailedTitle"
            notFoundMeansNoAccount={false}
            onRetry={() => upcomingQuery.refetch()}
          />
        )}

        {wallet && walletQuery.isError && (
          <WalletStaleNotice
            updatedAt={walletQuery.dataUpdatedAt}
            onRetry={() => walletQuery.refetch()}
          />
        )}

        {(wallet || upcoming) && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4">
            {wallet && (
              <WalletKpiTile
                icon={<WalletIcon />}
                label={t('pxtraCard:balance')}
                value={formatCents(wallet.balance_cents)}
                subtext={t('pxtraCard:balanceAsOf', {
                  date: formatDate(
                    wallet.balance_as_of ?? new Date(walletQuery.dataUpdatedAt).toISOString()
                  )
                })}
              />
            )}
            {upcoming && (
              <>
                <WalletKpiTile
                  icon={<TimeCoinsIcon />}
                  label={t('pxtraCard:upcomingPayments')}
                  value={formatCents(upcoming.total_cents)}
                  subtext={
                    upcoming.positions_count
                      ? upcomingSummary(t, upcoming.positions_count, upcoming.recipients_total)
                      : t('pxtraCard:noPaymentsPlanned')
                  }
                />
                <WalletKpiTile
                  icon={upcoming.shortfall_cents > 0 ? <ClockIcon /> : <CoinGrowthOutlineIcon />}
                  label={t('pxtraCard:shortfall')}
                  value={formatCents(upcoming.shortfall_cents)}
                  subtext={shortfallSubtext()}
                  compactIcon={upcoming.shortfall_cents === 0}
                  tone={upcoming.shortfall_cents > 0 ? 'danger' : 'default'}
                />
              </>
            )}
          </div>
        )}

        {wallet && upcoming && (
          <WalletAlert upcoming={upcoming} balanceCents={wallet.balance_cents} />
        )}

        {(upcoming || !upcomingQuery.isError) && (
          <section className="min-w-0 overflow-hidden rounded-2xl border border-solid border-grey-100 bg-white">
            <header className="flex items-center gap-2 p-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-grey-50 text-primary-main">
                <TimeCoinsIcon />
              </span>
              <span className="text-lg font-medium text-primary-main">
                {t('pxtraCard:upcomingPayments')}
              </span>
            </header>
            {upcoming ? <UpcomingPaymentsTable upcoming={upcoming} /> : <Loading />}
          </section>
        )}

        {transactionsQuery.isError && !transactions && (
          <WalletLoadError
            error={transactionsQuery.error}
            titleKey="pxtraCard:transactionsLoadFailedTitle"
            notFoundMeansNoAccount={false}
            onRetry={() => transactionsQuery.refetch()}
          />
        )}
        {!transactions && !transactionsQuery.isError && <Loading />}
        {transactions && (
          <TransactionsCard
            key={`current-${year}`}
            year={year}
            transactions={transactions}
            currentMonth={month}
            defaultExpanded
            onDownload={onDownload}
          />
        )}
        {previousQuery.isError && !previous && (
          <WalletLoadError
            error={previousQuery.error}
            titleKey="pxtraCard:transactionsLoadFailedTitle"
            notFoundMeansNoAccount={false}
            onRetry={() => previousQuery.refetch()}
          />
        )}
        {previous && (
          <TransactionsCard
            key={`previous-${year - 1}`}
            year={year - 1}
            transactions={previous}
            currentMonth={month}
            defaultExpanded={false}
            onDownload={onDownload}
          />
        )}
      </div>
    </>
  );
});

export const CardWallet = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const queryClient = useQueryClient();
  const companyId = companyStore.company?.id ?? '';
  const today = useToday();
  const [refreshing, setRefreshing] = useState(false);

  // The wallet endpoints answer 404 until Swan has opened the account; the
  // onboarding notice explains the wait instead.
  const onboardingQuery = useCardOnboarding(companyId);
  const { data: onboarding, isLoading } = onboardingQuery;
  const isVerified = onboarding?.phase === 'verified';

  const refresh = async () => {
    setRefreshing(true);
    try {
      await invalidateWallet(queryClient, companyId);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('pxtraCard:wallet')}
          subtitle={t('pxtraCard:walletSubtitle')}
          showActions={isVerified}
          renderActions={() => (
            <Button
              className="shrink-0 whitespace-nowrap"
              variant="primary"
              size="small"
              disabled={refreshing}
              aria-busy={refreshing}
              onClick={refresh}
            >
              {t('pxtraCard:refresh')}
            </Button>
          )}
        />
      }
    >
      <CardOnboardingNotice />
      {isLoading && <Loading />}
      {onboardingQuery.isError && !onboarding && (
        <WalletLoadError
          error={onboardingQuery.error}
          notFoundMeansNoAccount={false}
          onRetry={() => onboardingQuery.refetch()}
        />
      )}
      {isVerified && <WalletOverview companyId={companyId} today={today} />}
    </ContentBox>
  );
});
